import kotlinx.coroutines.flow.StateFlow

// Entitlement identifiers configured in RevenueCat. The product/price for each comes from the
// current Offering's packages (looked up by the package identifiers below), which lets us re-price
// remotely and run RevenueCat price Experiments without an app update.
internal const val REMOVE_ADS_ENTITLEMENT_ID = "remove_ads"
internal const val BANKO_PLUS_ENTITLEMENT_ID = "banko_plus"

// Package identifiers within the current Offering. Configure these in the RevenueCat dashboard.
internal const val REMOVE_ADS_PACKAGE_ID = "\$rc_lifetime"
internal const val BANKO_PLUS_MONTHLY_PACKAGE_ID = "\$rc_monthly"
internal const val BANKO_PLUS_ANNUAL_PACKAGE_ID = "\$rc_annual"

private const val STORE_CONFIGURATION_MESSAGE =
    "Purchase options are not available right now. Please try again later."
private const val STORE_PARTIAL_CONFIGURATION_MESSAGE =
    "Some purchase options are not available right now. Please try again later."
private const val STORE_NETWORK_MESSAGE =
    "Purchase options could not load. Check your internet connection and try again."
private const val STORE_DEVICE_TIME_MESSAGE =
    "Purchase options could not load. Check that your device date and time are set automatically, then try again."
private const val PURCHASE_NETWORK_MESSAGE =
    "Purchases could not connect. Check your internet connection and try again."
private const val PURCHASE_DEVICE_TIME_MESSAGE =
    "Purchases could not connect. Check that your device date and time are set automatically, then try again."

private enum class PurchaseConnectionProblem {
    DeviceTime,
    Network,
    None,
}

enum class BankoPlusPlan {
    Monthly,
    Yearly,
}

data class RemoveAdsPurchaseUiState(
    // True when ads should be hidden for THIS user — either the one-time remove-ads purchase or an
    // active Banko Plus subscription (Plus implies your own ads are gone too).
    val adsRemoved: Boolean = false,
    // True when this user holds an active Banko Plus subscription (host perks, premium settings).
    val bankoPlusActive: Boolean = false,
    val bankoPlusPlan: BankoPlusPlan? = null,
    val subscriptionManagementUrl: String? = null,
    val isPurchasesSupported: Boolean = true,
    val isConfigured: Boolean = false,
    // True after the first customer-info refresh succeeds or fails. Ads wait for this so payers do
    // not issue a live ad request during RevenueCat cold start.
    val entitlementsResolved: Boolean = false,
    // Bumped on every applied customer-info refresh, so a refresh with unchanged entitlements still
    // emits (AppStartup re-checks limited-time dice then, e.g. after a return to the foreground).
    val entitlementsRevision: Int = 0,
    val isLoading: Boolean = false,
    val priceLabel: String? = null,
    val bankoPlusPriceLabel: String? = null,
    val bankoPlusAnnualPriceLabel: String? = null,
    val catalogMessage: String? = null,
    val message: String? = null,
)

expect object RemoveAdsPurchases {
    val uiState: StateFlow<RemoveAdsPurchaseUiState>

    fun configure(adsRemovedHint: Boolean, onAdsRemovedChanged: (Boolean) -> Unit)
    fun purchaseRemoveAds()
    fun purchaseBankoPlus()
    fun purchaseBankoPlusAnnual()
    fun restorePurchases()
    fun refreshEntitlements()
    fun clearMessage()
}

internal fun unavailableStoreMessage(packagesLoaded: Boolean, missingPackages: List<String>): String? = when {
    !packagesLoaded -> STORE_CONFIGURATION_MESSAGE
    missingPackages.isNotEmpty() -> STORE_PARTIAL_CONFIGURATION_MESSAGE
    else -> null
}

internal fun Throwable.storeLoadMessage(): String {
    return when (connectionProblem()) {
        PurchaseConnectionProblem.DeviceTime -> STORE_DEVICE_TIME_MESSAGE
        PurchaseConnectionProblem.Network -> STORE_NETWORK_MESSAGE
        PurchaseConnectionProblem.None -> STORE_CONFIGURATION_MESSAGE
    }
}

internal fun Throwable.purchaseActionMessage(fallback: String): String {
    return when (connectionProblem()) {
        PurchaseConnectionProblem.DeviceTime -> PURCHASE_DEVICE_TIME_MESSAGE
        PurchaseConnectionProblem.Network -> PURCHASE_NETWORK_MESSAGE
        PurchaseConnectionProblem.None -> message ?: fallback
    }
}

private fun Throwable.connectionProblem(): PurchaseConnectionProblem {
    val details = messageChain().lowercase()
    return when {
        details.contains("chain validation") ||
            details.contains("certificate") ||
            details.contains("sslhandshake") ||
            details.contains("certpath") ||
            details.contains("trust anchor") -> PurchaseConnectionProblem.DeviceTime
        details.contains("network") ||
            details.contains("timeout") ||
            details.contains("connection") ||
            details.contains("host") ||
            details.contains("internet") -> PurchaseConnectionProblem.Network
        else -> PurchaseConnectionProblem.None
    }
}

private fun Throwable.messageChain(): String {
    val messages = mutableListOf<String>()
    var current: Throwable? = this
    while (current != null) {
        current.message?.let(messages::add)
        current = current.cause
    }
    return messages.joinToString(separator = " ")
}
